import { useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { Plus } from "lucide-react";
import { useTranslationApp } from "../../lib/i18next";
import {
  MODAL_IMAGE_EDIT_LAYERS_ADD,
  TEMPLATES_PRODUCE_ADD_GROUP,
  TEMPLATES_PRODUCE_ADD_IMAGE,
  TEMPLATES_PRODUCE_ADD_SHAPE,
  TEMPLATES_PRODUCE_ADD_TEXT,
} from "../../i18n/keysTranslations";
import ModalGalleryAssets from "../Modal/ModalGalleryAssets";

const CANVAS_ADD_LAYER_KIND = {
  TEXT: "text",
  IMAGE: "image",
  SHAPE: "shape",
  GROUP: "group",
};

const TOOLS_BAR_BORDER = "#D4D4D8";
const TOOLS_BAR_ICON = "#18181B";
const TOOLS_BAR_ACTIVE_BG = "#18181B";
const TOOLS_BAR_ACTIVE_ICON = "#FFFFFF";
const TOOLS_BAR_HOVER_BG = "rgba(24,24,27,0.06)";
const TOOLS_BAR_SEPARATOR = "rgba(24,24,27,0.16)";
const TOOL_BUTTON_SIZE = 36;
const CANVAS_TOOLS_BAR_VISIBLE = false;

const getAddLayerKindLabelKey = (kind) => {
  switch (kind) {
    case CANVAS_ADD_LAYER_KIND.TEXT:
      return TEMPLATES_PRODUCE_ADD_TEXT;
    case CANVAS_ADD_LAYER_KIND.IMAGE:
      return TEMPLATES_PRODUCE_ADD_IMAGE;
    case CANVAS_ADD_LAYER_KIND.SHAPE:
      return TEMPLATES_PRODUCE_ADD_SHAPE;
    case CANVAS_ADD_LAYER_KIND.GROUP:
      return TEMPLATES_PRODUCE_ADD_GROUP;
    default: {
      const exhaustive = kind;
      throw new Error(`Unhandled add layer kind: ${exhaustive}`);
    }
  }
};

export const CanvasToolsBar = ({
  orientation = "horizontal",
  children,
  "aria-label": ariaLabel,
}) => {
  if (!CANVAS_TOOLS_BAR_VISIBLE) return null;
  const isVertical = orientation === "vertical";
  return (
    <Box
      role="toolbar"
      aria-label={ariaLabel}
      aria-orientation={isVertical ? "vertical" : "horizontal"}
      onPointerDown={(event) => event.stopPropagation()}
      sx={{
        display: "inline-flex",
        flexDirection: isVertical ? "column" : "row",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 0.5,
        minWidth: 260,
        justifyContent: "center",
        bgcolor: "transparent",
        border: `1px solid ${TOOLS_BAR_BORDER}`,
        borderRadius: "12px",
      }}
    >
      {children}
    </Box>
  );
};

export const CanvasToolsBarButton = ({
  icon: Icon,
  label,
  showLabel = false,
  active = false,
  disabled = false,
  loading = false,
  onClick = () => {},
  ...buttonProps
}) => {
  const isDisabled = disabled || loading;
  const button = (
    <Box
      component="button"
      type="button"
      aria-label={label}
      aria-pressed={active}
      disabled={isDisabled}
      onClick={onClick}
      {...buttonProps}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: showLabel ? 0.75 : 0,
        minWidth: showLabel ? "auto" : TOOL_BUTTON_SIZE,
        height: TOOL_BUTTON_SIZE,
        px: showLabel ? 1.5 : 0,
        border: 0,
        borderRadius: "8px",
        bgcolor: active ? TOOLS_BAR_ACTIVE_BG : "transparent",
        color: active ? TOOLS_BAR_ACTIVE_ICON : TOOLS_BAR_ICON,
        cursor: isDisabled ? "default" : "pointer",
        appearance: "none",
        fontFamily: "inherit",
        lineHeight: 1,
        flexShrink: 0,
        "&:hover:not(:disabled)": {
          bgcolor: active ? TOOLS_BAR_ACTIVE_BG : TOOLS_BAR_HOVER_BG,
        },
        "&:disabled": {
          opacity: 0.4,
          cursor: "default",
        },
      }}
    >
      {loading ? (
        <CircularProgress size={16} sx={{ color: "inherit" }} />
      ) : (
        <Icon size={18} strokeWidth={1.75} />
      )}
      {showLabel && !loading ? (
        <Box
          component="span"
          sx={{
            fontSize: 13,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Box>
      ) : null}
    </Box>
  );

  if (showLabel) return button;

  return (
    <Tooltip title={label}>
      <span style={{ display: "inline-flex" }}>{button}</span>
    </Tooltip>
  );
};

export const CanvasToolsBarSeparator = ({ orientation = "horizontal" }) => {
  const isVertical = orientation === "vertical";
  return (
    <Box
      role="separator"
      sx={{
        width: isVertical ? 18 : "1px",
        height: isVertical ? "1px" : 18,
        bgcolor: TOOLS_BAR_SEPARATOR,
        flexShrink: 0,
        mx: isVertical ? 0 : 0.25,
        my: isVertical ? 0.25 : 0,
      }}
    />
  );
};

export const CanvasAddLayerButton = ({
  kinds = [CANVAS_ADD_LAYER_KIND.TEXT, CANVAS_ADD_LAYER_KIND.IMAGE],
  onAdd = () => {},
  disabled = false,
  label,
}) => {
  const { t } = useTranslationApp();
  const [menuEl, setMenuEl] = useState(null);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const addLabel = label || t(MODAL_IMAGE_EDIT_LAYERS_ADD);

  const onChooseKind = (kind) => {
    setMenuEl(null);
    switch (kind) {
      case CANVAS_ADD_LAYER_KIND.IMAGE:
        setIsGalleryOpen(true);
        return;
      case CANVAS_ADD_LAYER_KIND.TEXT:
      case CANVAS_ADD_LAYER_KIND.SHAPE:
      case CANVAS_ADD_LAYER_KIND.GROUP:
        onAdd({ kind });
        return;
      default: {
        const exhaustive = kind;
        throw new Error(`Unhandled add layer kind: ${exhaustive}`);
      }
    }
  };

  const onSelectGalleryItems = async (selectedItems) => {
    const urls = (selectedItems || []).map((item) => item?.url).filter(Boolean);
    if (!urls.length) return;
    setIsAdding(true);
    try {
      await onAdd({ kind: CANVAS_ADD_LAYER_KIND.IMAGE, urls });
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <>
      <CanvasToolsBarButton
        icon={Plus}
        label={addLabel}
        active={Boolean(menuEl)}
        disabled={disabled}
        loading={isAdding}
        aria-haspopup="menu"
        aria-expanded={Boolean(menuEl)}
        onClick={(event) => {
          if (disabled || isAdding) return;
          setMenuEl(event.currentTarget);
        }}
      />
      <Menu
        anchorEl={menuEl}
        open={Boolean(menuEl)}
        onClose={() => setMenuEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        {kinds.map((kind) => (
          <MenuItem key={kind} onClick={() => onChooseKind(kind)}>
            {t(getAddLayerKindLabelKey(kind))}
          </MenuItem>
        ))}
      </Menu>
      {isGalleryOpen ? (
        <ModalGalleryAssets
          modalOpen={isGalleryOpen}
          multiple
          onCloseModal={() => setIsGalleryOpen(false)}
          onCallbackSelectedItems={onSelectGalleryItems}
        />
      ) : null}
    </>
  );
};
